import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Dialog, IconButton, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { PROJECTS, ProjectInfo, SECTIONS } from "./projectsData";
import "../Css/Projects.css";

export function Projects() {
    const [active, setActive] = useState<ProjectInfo | null>(null);
    const fullScreen = useMediaQuery("(max-width: 767px)");
    const ActiveComponent = active ? active.component : null;

    return (
        <div className="projects-container">
            <section className="projects-hero">
                <span className="projects-eyebrow">Live demos</span>
                <h1>Projects</h1>
                <p>
                    Small, fully working examples of what we build. Click any preview to try it right here,
                    or open it on its own page.
                </p>
            </section>

            {SECTIONS.map(section => {
                const projects = PROJECTS.filter(p => p.section === section.id);
                if (projects.length === 0) {
                    return null;
                }
                return (
                    <section key={section.id} className="projects-section" aria-labelledby={`projects-${section.id}`}>
                        <div className="projects-section-head">
                            <h2 id={`projects-${section.id}`}>{section.title}</h2>
                            <p>{section.description}</p>
                        </div>
                        <div className="projects-grid">
                            {projects.map((project, index) => (
                                <article
                                    key={project.slug}
                                    className="project-card"
                                    style={{ "--project-accent": project.accent, animationDelay: `${PROJECTS.indexOf(project) * 0.1}s` } as React.CSSProperties}
                                >
                                    <button className="project-preview" onClick={() => setActive(project)} aria-label={`Preview ${project.title}`}>
                                        <img src={project.image} alt={`${project.title} preview`} />
                                        <span className="project-preview-overlay">
                                            <span className="project-play">
                                                <PlayArrowRoundedIcon />
                                            </span>
                                            Live preview
                                        </span>
                                    </button>
                                    <div className="project-body">
                                        <span className="project-number">0{index + 1}</span>
                                        <h3>{project.title}</h3>
                                        <p className="project-tagline">{project.tagline}</p>
                                        <p className="project-description">{project.description}</p>
                                        <div className="project-tags">
                                            {project.tags.map(tag => <span key={tag}>{tag}</span>)}
                                        </div>
                                        <div className="project-actions">
                                            <Button variant="contained" className="project-btn" onClick={() => setActive(project)}>
                                                Preview
                                            </Button>
                                            <Button
                                                component={Link}
                                                to={`/projects/${project.slug}`}
                                                endIcon={<OpenInNewIcon fontSize="small" />}
                                                className="project-link"
                                            >
                                                Open page
                                            </Button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </section>
                );
            })}

            <Dialog
                open={active !== null}
                onClose={() => setActive(null)}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="lg"
                PaperProps={{ className: "project-dialog" }}
            >
                {active && ActiveComponent && (
                    <>
                        <div className="project-dialog-bar">
                            <div className="project-dialog-dots" aria-hidden="true">
                                <span /><span /><span />
                            </div>
                            <div className="project-dialog-url">
                                {window.location.host}/projects/{active.slug}
                            </div>
                            <Button
                                size="small"
                                component={Link}
                                to={`/projects/${active.slug}`}
                                endIcon={<OpenInNewIcon fontSize="small" />}
                                className="project-dialog-open"
                            >
                                Full page
                            </Button>
                            <IconButton size="small" onClick={() => setActive(null)} aria-label="Close preview">
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <div className="project-dialog-content">
                            <ActiveComponent />
                        </div>
                    </>
                )}
            </Dialog>
        </div>
    );
}
